import { FC } from "react";
import { useTranslation } from "react-i18next";

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/_atoms/tooltip";
import { cn } from "@/lib/cn";

import { displayNameForUser } from "../models/display-name-for-user";
import { type User } from "../models/user-model";

import { UserAvatar } from "./user-avatar";

const MAX_VISIBLE_AVATAR_COUNT = 3;

interface AssigneeAvatarGroupProps {
  userList: readonly User[];
  showName?: boolean;
  className?: string;
}

export const AssigneeAvatarGroup: FC<AssigneeAvatarGroupProps> = ({ userList, showName = false, className }) => {
  const { t } = useTranslation("user");

  if (userList.length === 0) {
    return null;
  }

  if (userList.length === 1) {
    const user = userList[0];
    const displayName = displayNameForUser(user);

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className={cn("flex items-center gap-1", className)}>
              <UserAvatar user={user} size="xs" />
              {showName && <span className="truncate text-ellipsis whitespace-nowrap">{displayName}</span>}
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>{displayName}</p>
            {user.email && <p className="text-xs text-muted-foreground">{user.email}</p>}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  const visibleUserList = userList.slice(0, MAX_VISIBLE_AVATAR_COUNT);
  const hiddenUserCount = userList.length - visibleUserList.length;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={cn("flex items-center", className)}>
            <div className="flex -space-x-1.5">
              {visibleUserList.map((user, index) => (
                <UserAvatar
                  key={user.id}
                  user={user}
                  size="xs"
                  className={cn("ring-1 ring-background", index > 0 && "-ml-1.5")}
                />
              ))}
            </div>

            {hiddenUserCount > 0 && <span className="ml-1 text-xs text-muted-foreground">+{hiddenUserCount}</span>}

            {showName && (
              <span className="ml-2 truncate text-ellipsis whitespace-nowrap">
                {t("assignee.count", { count: userList.length })}
              </span>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <div className="flex flex-col gap-2">
            {userList.map((user) => (
              <div key={user.id} className="flex items-center gap-2">
                <UserAvatar user={user} size="xs" />
                <span className="text-sm">{displayNameForUser(user)}</span>
              </div>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
